import {
  Box,
  CircularProgress,
  DragDrop,
  FormControlLabel,
  GKButton,
  GKCheckbox,
  GKClientDataGrid,
  GKEditor,
  GKModal,
  GKSearchSelect,
  GKSideModal,
  GKTextField,
  Grid2,
  ImportIcon,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { removeUnderscoreAndFirstLetterCapital } from "utils";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, refetchQuery } from "utils/helpers";
import { validateNotWhitespaceOnly } from "utils/validationSchema";

interface EmailTemplate {
  id: string;
  subject?: string;
  body?: string;
  includeSignature?: boolean;
  includeFooter?: boolean;
  emailCategory?: string;
  file?: File | string | null;
  editable?: boolean;
  gridkeyTemplate: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  template: EmailTemplate | null;
}

interface EmailTemplateFormValues {
  subject: string;
  body: string;
  includeSignature: boolean;
  includeFooter: boolean;
  file: File | null;
  emailCategory: number | string;
  gridkeyTemplate: boolean;
}

const EmailTemplateEditorModal = (props: Props) => {
  const { open, onClose, template } = props;

  const editorRef = useRef<any>(null);

  const [fileRemoved, setFileRemoved] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [uploadError, setUploadError] = useState<string | null>(null);

  const form = useForm<EmailTemplateFormValues>({
    defaultValues: {
      subject: "",
      body: "",
      includeSignature: false,
      includeFooter: false,
      file: null,
      emailCategory: "",
      gridkeyTemplate: false,
    },
    values: {
      subject: template?.subject || "",
      body: template?.body || "",
      includeSignature: template?.includeSignature || false,
      includeFooter: template?.includeFooter || false,
      file: template?.file
        ? typeof template.file === "string"
          ? new File([], template.file.split("/").pop()?.split("?")[0] || "", {
              type: "application/octet-stream",
            })
          : template.file
        : null,
      emailCategory: template?.emailCategory || "",
      gridkeyTemplate: template?.gridkeyTemplate || false,
    },
  });

  form.watch("body");

  const { data: EmailVariables } = useFetchQuery({
    key: ["GET_EMAIL_VARIABLES"],
    route: "/email-variables/",
  });

  const { data: Templates } = useFetchQuery({
    key: ["GET_EMAIL_TEMPLATES"],
    route: "/email-templates/",
  });

  const { mutate: emailTemplates, isLoading: isSending } = useMutation({
    mutationFn: (data: FormData) =>
      axiosInstance.post(`/email-templates/`, data),
    mutationKey: ["EMAIL_TEMPLATES"],
    onSuccess: () => {
      onClose();
      refetchQuery("GET_EMAIL_TEMPLATES");
      form.reset();
    },
  });

  const { mutate: updateEmailTemplates, isLoading: isUpdating } = useMutation({
    mutationFn: (data: FormData) =>
      axiosInstance.patch(`/email-templates/${template?.id}/`, data),
    mutationKey: ["EMAIL_TEMPLATES"],
    onSuccess: () => {
      onClose();
      refetchQuery("GET_EMAIL_TEMPLATES");
      form.reset();
    },
  });

  const getTemplateBody = (emailCategoryId: string | number) => {
    const template = Templates?.data?.data?.find(
      (t: any) => t.emailCategory === emailCategoryId && t.editable === false,
    );
    return template?.body || "";
  };

  const appendVariable = (variableValue: string) => {
    if (!editorRef.current) {
      console.error("Editor reference is not available");
      return;
    }

    const editor = editorRef && (editorRef?.current as any);

    editor.model.change((writer: any) => {
      const { selection } = editor.model.document;
      const position = selection.getFirstPosition();
      writer.insertText(`{{${variableValue}}}`, position);
    });
  };

  const onSubmit = (values: EmailTemplateFormValues) => {
    const formData = new FormData();
    formData.append("subject", values.subject);
    formData.append("body", values.body);
    formData.append("includeSignature", values.includeSignature.toString());
    formData.append("includeFooter", values.includeFooter.toString());
    formData.append("emailCategory", values.emailCategory.toString());
    formData.append("gridkeyTemplate", values.gridkeyTemplate.toString());

    if (values.file instanceof File && values.file.size > 0) {
      formData.append("file", values.file);
    } else if (fileRemoved && template?.file) {
      formData.append("file", "");
    }

    if (template) {
      updateEmailTemplates(formData);
    } else {
      emailTemplates(formData);
    }
  };

  const selectedCategory = EmailVariables?.data?.data?.find(
    (item: any) => item.id === form.watch("emailCategory"),
  );
  const hideGridkeyButton = selectedCategory?.emailType !== "general";

  return (
    <GKSideModal
      open={open}
      setOpen={onClose}
      onClose={() => {
        form.reset();
        onClose();
      }}
      width="100%"
      modalTitle={template?.subject || "Email Template"}
      titleComponent={
        hideGridkeyButton ? (
          <GKButton
            variant="outlined"
            size="small"
            startIcon={<ImportIcon width={16} height={16} />}
            onClick={() => setIsModalOpen(true)}
            disabled={Boolean(template)}
          >
            Gridkey Template
          </GKButton>
        ) : null
      }
      footerButton={
        <Grid2 container justifyContent={"flex-end"} gap={1}>
          <GKButton
            variant="contained"
            size="medium"
            type="submit"
            form="email-template-form"
            // disabled={!!(template && template.editable === false)}
          >
            {isSending || isUpdating ? (
              <CircularProgress size={20} />
            ) : (
              <>{template ? "Update Template" : "Save Template"}</>
            )}
          </GKButton>
        </Grid2>
      }
    >
      <form id="email-template-form" onSubmit={form.handleSubmit(onSubmit)}>
        <Grid2 container size={12} spacing={2}>
          <Grid2 size={4}>
            <Controller
              name="emailCategory"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select a email type",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="Email Template Type"
                  requiredField
                  disableClearable
                  error={!!error}
                  helperText={error?.message}
                  disabled={Boolean(template)}
                  value={removeUnderscoreAndFirstLetterCapital(
                    EmailVariables?.data?.data?.find(
                      (item: any) => item.id === field.value,
                    )?.emailType || null,
                  )}
                  options={
                    EmailVariables?.data?.data
                      ?.filter(
                        (item: any) =>
                          !["stock_recommendation", "order_execution"].includes(
                            item.emailType,
                          ),
                      )
                      ?.map((dataItem: any) => ({
                        ...dataItem,
                        emailType: removeUnderscoreAndFirstLetterCapital(
                          dataItem?.emailType,
                        ),
                      })) || []
                  }
                  getOptionLabel={(e) =>
                    removeUnderscoreAndFirstLetterCapital(e?.emailType) || ""
                  }
                  onChange={(e, val) => {
                    field.onChange(val?.id);
                    form.setValue("body", "");
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.emailType}</li>
                  )}
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="subject"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter subject",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Subject"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                  disabled={!!(template && template.editable === false)}
                  fullWidth
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Typography>Variables for Body</Typography>
            <Box>
              {EmailVariables?.data?.data
                ?.find((item: any) => item.id === form.watch("emailCategory"))
                ?.variables?.map((variable: any) => (
                  <GKButton
                    sx={{
                      margin: "4px",
                      "& .MuiButton-icon": {
                        marginRight: "2px",
                      },
                    }}
                    variant="outlined"
                    color="dark"
                    size="extraSmall"
                    key={variable.value}
                    onClick={() => appendVariable(variable.value)}
                    disabled={!!(template && template.editable === false)}
                  >
                    {`${variable.name}`}
                  </GKButton>
                ))}
            </Box>
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="body"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter email body",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <Box sx={{ mt: 2 }}>
                  <GKEditor
                    inputLabel="Body"
                    value={field.value}
                    disabled={!!(template && template.editable === false)}
                    onChange={(val: string) => field.onChange(val)}
                    onReady={(editor: any) => {
                      editorRef.current = editor;
                    }}
                    isEmailEditor
                    requiredField
                    error={!!error}
                    helperText={error?.message}
                  />
                </Box>
              )}
            />
          </Grid2>

          <Grid2 size={12} mt={2}>
            <Typography>Add Attachment</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Controller
              control={form.control}
              name="file"
              render={({ field }) => (
                <DragDrop
                  {...field}
                  uploadError={uploadError}
                  setUploadError={setUploadError}
                  value={field.value ? [field.value] : []}
                  onChange={(files) => {
                    const file = files?.[0] ?? null;
                    setFileRemoved(!file);
                    field.onChange(file);
                  }}
                  acceptType=".pdf,.doc,.docx,.xls,.xlsx"
                  supportName="PDF, DOC, DOCX, XLS, XLSX"
                  multiple={false}
                />
              )}
            />
          </Grid2>

          {/* {!form.watch("gridkeyTemplate") && ( */}
          <>
            <Grid2 size={4}>
              <Controller
                name="includeSignature"
                control={form.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <GKCheckbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    }
                    label="Include Signature"
                  />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="includeFooter"
                control={form.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <GKCheckbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    }
                    label="Include Footer"
                  />
                )}
              />
            </Grid2>
          </>
          {/* )} */}
        </Grid2>
        <GKModal
          open={isModalOpen}
          setOpen={setIsModalOpen}
          onClose={() => {
            form.reset();
            onClose();
          }}
          maxHeight="480px"
          modalTitle="Gridkey Template"
        >
          <GKClientDataGrid
            name="gridkeyTemplate"
            rows={
              EmailVariables?.data?.data?.filter(
                (item: any) =>
                  ![
                    "stock_recommendation",
                    "order_execution",
                    "general",
                  ].includes(item.emailType),
              ) || []
            }
            columns={[
              {
                ...extraColumnsField("Template Name", "emailType", 100, 1),
                valueGetter: (params: any) =>
                  removeUnderscoreAndFirstLetterCapital(params.row.emailType),
              },
            ]}
            onRowClick={(params) => {
              form.setValue("emailCategory", params.row.id);
              form.setValue("body", getTemplateBody(params.row.id));
              form.setValue("gridkeyTemplate", true);
              setIsModalOpen(false);
              form.setValue("includeSignature", false);
              form.setValue("includeFooter", false);
            }}
          />
        </GKModal>
      </form>
    </GKSideModal>
  );
};

export default EmailTemplateEditorModal;
